'use client';

import { useTranslation } from 'react-i18next';
import { useBlockPart, useMediaSource, type MediaVideoDetail } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { EmptyState } from '../_shared/EmptyState';
import { CollapsibleDescription } from '../ui/CollapsibleDescription';
import { ChannelChip } from './ChannelChip';
import { SyntheticChannelChip } from './SyntheticChannelChip';
import { WatchLaterButton } from '../islands/WatchLaterButton';
import { LikeButton } from '../islands/LikeButton';
import { CommentsBlock } from './CommentsBlock';
import { HistoryWriter } from './HistoryWriter';
import { sectionPad, sectionInner, kickerStyle } from '../_shared/styles';
import { VideoCard } from './VideoCard';
import { Cinema8Player } from './Cinema8Player';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Standalone /video/[slug] watch page. Player frame, title, channel
 * chip, action group (Watch later), description, related rail.
 */
export interface VideoDetailLayoutProps {
  slug: string;
  /** Whether to render the player frame. When false, the page is the
   *  "video data card" without an embedded player (admin previews
   *  use this). */
  showPlayer?: boolean;
  videoHrefTemplate?: string;
}

export function VideoDetailLayout({
  slug,
  showPlayer = true,
  videoHrefTemplate = '/tube/{slug}',
}: VideoDetailLayoutProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const media = useMediaSource();
  const state = useAsyncResource<MediaVideoDetail>(
    async (signal) => {
      if (!media) return null;
      return await media.videos.fetchBySlug(slug, signal);
    },
    [media, slug],
  );

  if (!media) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="VideoDetailLayout" adapterName="MediaSource" />
        </div>
      </BlockRoot>
    );
  }
  if (state.kind === 'loading') return null;
  if (state.kind !== 'loaded') {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <EmptyState
            {...part('empty')}
            title={
              state.kind === 'error'
                ? t('videoDetail.error.title')
                : t('videoDetail.notFound.title')
            }
            hint={
              state.kind === 'error'
                ? t('shared.error.tryAgainLater')
                : t('shared.error.removedOrUnpublished')
            }
          />
        </div>
      </BlockRoot>
    );
  }
  const v = state.data;
  const related = v.related ?? [];

  return (
    <BlockRoot as="article" style={sectionPad}>
      <div style={sectionInner}>
        {/* Watching a video = history entry (player pages only, not admin previews). */}
        {showPlayer && <HistoryWriter videoSlug={v.slug} />}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 'var(--space-6, 24px)',
          }}
        >
          {/* Left: player + meta + description */}
          <div
            style={{
              flex: '3 1 520px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4, 16px)',
            }}
          >
            {showPlayer && (
              <Cinema8Player
                {...(v.mediaId ? { mediaId: v.mediaId } : {})}
                {...(v.playerEmbedUrl ? { embedUrl: v.playerEmbedUrl } : {})}
                {...(v.videoUrl ? { videoUrl: v.videoUrl } : {})}
                {...(v.posterUrl ? { poster: v.posterUrl } : {})}
                title={v.title ?? 'Video'}
              />
            )}
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--font-size-2xl, 26px)',
                fontWeight: 700,
                lineHeight: 1.25,
                letterSpacing: 'var(--font-tracking-tight, -0.01em)',
                color: 'var(--color-text-primary, #18181b)',
              }}
            >
              {v.title}
            </h1>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              {v.channelSlug && v.channelName ? (
                <ChannelChip
                  channelSlug={v.channelSlug}
                  channelName={v.channelName}
                  channelAvatar={v.channelAvatar ?? undefined}
                  {...(v.channelSubscriberCount !== undefined
                    ? { subscriberCount: v.channelSubscriberCount }
                    : {})}
                />
              ) : v.director ? (
                <SyntheticChannelChip uploaderName={v.director} />
              ) : (
                <span />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)' }}>
                <LikeButton videoSlug={v.slug} />
                <WatchLaterButton videoSlug={v.slug} />
              </div>
            </div>
            <CollapsibleDescription
              header={
                <span>
                  {v.releaseYear && <>{v.releaseYear} · </>}
                  {v.durationLabel}
                </span>
              }
              body={v.description}
              {...(v.tags ? { tags: v.tags } : {})}
              {...(v.director ? { directorLine: `Directed by ${v.director}` } : {})}
            />
            {/* Comments live under the description (player pages only —
                admin previews render the card without user surfaces). */}
            {showPlayer && <CommentsBlock videoSlug={v.slug} />}
          </div>

          {/* Right: up-next rail (wraps below on narrow viewports) */}
          {related.length > 0 && (
            <aside
              style={{
                flex: '1 1 300px',
                minWidth: 280,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3, 12px)',
              }}
            >
              <div style={kickerStyle}>{t('videoDetail.upNext')}</div>
              <div
                {...part('list')}
                style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3-5, 14px)' }}
              >
                {related.map((item) => (
                  <VideoCard
                    key={item.id}
                    item={item}
                    variant="youtube"
                    {...(videoHrefTemplate ? { hrefTemplate: videoHrefTemplate } : {})}
                  />
                ))}
              </div>
            </aside>
          )}
        </div>
      </div>
    </BlockRoot>
  );
}
